import { Box, Button, Spinner, Stack, Text } from '@solvhealth/jigsaw-v1';
import Location from '@solvhealth/types/interfaces/Location';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PaperworkState } from '~/ducks/paperwork';
import useSetTitle from '~/hooks/useSetTitle';
import { analyticsTrackEvent } from '../../../../core/analytics';
import { PAPERWORK_LOG_IN_CLICK } from '../../../../core/analytics/events';
import withAuthentication from '../../../../core/login/withAuthentication';
import { isPaperworkCompleted } from '../../../../core/util/paperwork';
import StartPaperworkFrame from './components/StartPaperworkFrame';
import { isProviderGroupLocation } from '~/core/util/location';
import { useGoForwardAndBack, useEvent } from '../../hooks';
import { PGSectionNumber } from '../../providerGroups/constants';
import { SectionNumber } from '../../constants';
import { usePreloadedData } from '../../hooks/usePreloadedData';
import { usePaperworkContextBookingId } from '~/routes/paperwork/PaperworkBookingIdContext';

const title = 'Paperwork – Start';

type Props = {
  initializePaperworkData: (...args: any[]) => any;
  location?: Location;
  isLoggedIn?: boolean;
  loggingIn?: boolean;
  openLoginModal: (...args: any[]) => any;
  paperwork: PaperworkState;
  isAuthorized: boolean;
};

const PaperworkStart = ({
  initializePaperworkData,
  location,
  isLoggedIn,
  openLoginModal,
  paperwork,
  isAuthorized,
}: Props) => {
  const { booking, userProfile } = usePreloadedData();
  const { initializing } = paperwork;
  const hasCompletedPaperwork = isPaperworkCompleted(booking);
  const [clickedLogin, setClickedLogin] = useState(false);
  const isProviderGroup = isProviderGroupLocation(location);

  const bookingId = usePaperworkContextBookingId();

  useSetTitle(title);

  useEffect(() => {
    initializePaperworkData?.(bookingId, location);
  }, [bookingId, initializePaperworkData, location]);

  const [goForward] = useGoForwardAndBack(
    isProviderGroup ? PGSectionNumber.Start : SectionNumber.Start,
    isProviderGroup ? PGSectionNumber : SectionNumber,
    bookingId
  );

  /**
   * Routes to the proper paperwork page.
   *
   * Next page if just starting paperowrk. Finalize page if
   * it has already been completed
   */
  const goToPaperwork = useEvent(() => {
    goForward();

    // TODO: We want this behavior, but can't yet (see below)
    // const paperworkRoute = `${
    //   hasCompletedPaperwork
    //     ? PaperworkSectionRoutes.FINALIZE
    //     : getNextSectionRoute(PaperworkSectionRoutes.START, location, paperwork)
    // }/${bookingId}`;

    // // ! TECH DEBT: We should skip straight to the finalize page here, but it doesn't autofill data yet
  });

  /**
   * Watch if the user logs in (after having clicked a login button) and
   * redirect them to paperwork.
   */
  useEffect(() => {
    if (isLoggedIn && userProfile.is_verified && clickedLogin) goToPaperwork();
  }, [clickedLogin, isLoggedIn, userProfile.is_verified, goToPaperwork]);

  /**
   * If the user is already logged in, directs them to paperwork. Otherwise,
   * prompts a login and then redirects them to paperwork on success
   */
  const login = () => {
    if (isLoggedIn) {
      goToPaperwork();
      return;
    }

    setClickedLogin(true);
    analyticsTrackEvent(PAPERWORK_LOG_IN_CLICK, {
      bookingId,
      locationId: location && location.id,
      isLoggedIn,
    });
    openLoginModal();
  };

  const { t } = useTranslation(['common', 'paperwork']);

  if (initializing || typeof initializing === 'undefined') {
    return (
      <StartPaperworkFrame goToPaperwork={goToPaperwork} userProfile={userProfile}>
        <Stack alignItems="center" textAlign="center">
          <Spinner />
          <Text muted>{t('paperwork:start.loading')}</Text>
        </Stack>
      </StartPaperworkFrame>
    );
  }

  if (hasCompletedPaperwork) {
    return (
      <StartPaperworkFrame
        actions={
          <Button fullWidth loading={initializing} onClick={login}>
            {t('paperwork:start.edit.cta')}
          </Button>
        }
        goToPaperwork={goToPaperwork}
        userProfile={userProfile}
      >
        <Text variant="h2">
          {t(
            booking.firstName
              ? 'paperwork:start.edit.subtitlePersonal'
              : 'paperwork:start.edit.subtitleGeneric',
            {
              name: booking.firstName,
            }
          )}
        </Text>
        <Text muted>{t('paperwork:start.edit.makeChanges')}</Text>
      </StartPaperworkFrame>
    );
  }

  return (
    <StartPaperworkFrame
      actions={
        isLoggedIn ? (
          <Button fullWidth loading={initializing} onClick={goToPaperwork}>
            {t('paperwork:start.begin.cta')}
          </Button>
        ) : (
          <>
            <Button fullWidth loading={initializing} onClick={login}>
              {t('paperwork:start.begin.login')}
            </Button>
            <Box
              alignItems="center"
              display="flex"
              flexDirection="row"
              justifyContent="stretch"
              width="100%"
            >
              <Box borderBottom="solid 1px" borderColor="gray-200" flexGrow={1} />
              <Text muted px={2} variant="body2">
                {t('common:or')}
              </Text>
              <Box borderBottom="solid 1px" borderColor="gray-200" flexGrow={1} />
            </Box>
            <Button fullWidth loading={initializing} onClick={goToPaperwork} variant="outlined">
              {t('paperwork:start.begin.dontLogin')}
            </Button>
          </>
        )
      }
      goToPaperwork={goToPaperwork}
      userProfile={userProfile}
    >
      <Text variant="h2">
        {t(
          booking.firstName
            ? 'paperwork:start.begin.titlePersonal'
            : 'paperwork:start.begin.titleGeneric',
          {
            name: booking.firstName,
          }
        )}
      </Text>
      <Text muted>
        {isLoggedIn
          ? t('paperwork:start.begin.subtitleLoggedIn')
          : t('paperwork:start.begin.subtitleLoggedOut')}{' '}
      </Text>
    </StartPaperworkFrame>
  );
};

export default withAuthentication(PaperworkStart);
